/**
 * A question that opens inside something that scrolls -- "Pause it
 * too?", "Skip that run?", the delete dialog's "Delete them too?" --
 * comes into view and takes focus (2026-10-31).
 *
 * The laptop's floating schedule panel is short at 1280x720 and scrolls
 * on its own. With "Run next now" and "Skip next" above it, Pause's
 * question opened below what the panel showed: the press seemed to do
 * nothing, and a keyboard user's focus was left on a button the
 * question had replaced. Scrolled to the nearest edge, never centred, so
 * a question already in view does not move.
 *
 * ONLY THE NEAREST BOX THAT SCROLLS MOVES, and quietly. This used
 * `scrollIntoView`, which scrolls every box around the question until it
 * shows -- the lanes and the page included -- and the shell read the
 * page's move as the reader's and tucked the top bar and the Tasks title
 * away (a recorded limit of spa 0.5.68, fixed 2026-10-31). The box is
 * `data-tuck-quiet` while it moves, as the Agenda opening at now and the
 * Month sidebar's list are (`use-scroll-tuck.ts`).
 */
import { useEffect, useRef, type RefObject } from 'react';

/** The first button in `el` that can be pressed. */
export function firstEnabledButton(el: HTMLElement): HTMLButtonElement | null {
  return el.querySelector<HTMLButtonElement>('button:not([disabled])');
}

const SCROLLS = /^(auto|scroll|overlay)$/;

/** The nearest box around `el` that scrolls and has somewhere to scroll
 *  to, or null (then nothing around it can move). A fixed box -- a
 *  dialog's backdrop, a phone sheet -- ends the search: no scroll above
 *  it moves what is inside it. */
export function scrollBoxOf(el: HTMLElement): HTMLElement | null {
  for (let box = el.parentElement; box; box = box.parentElement) {
    const style = getComputedStyle(box);
    if (box.scrollHeight > box.clientHeight && SCROLLS.test(style.overflowY)) return box;
    if (style.position === 'fixed') return null;
  }
  return null;
}

/** How far a box scrolls for `el` to show in it: its end into view, but
 *  never its start out of it; 0 when it already shows. */
export function revealDelta(box: DOMRect, el: DOMRect): number {
  if (el.bottom > box.bottom) return Math.min(el.bottom - box.bottom, el.top - box.top);
  if (el.top < box.top) return el.top - box.top;
  return 0;
}

/** How long a box stays quiet: the scroll event of a jump arrives a
 *  frame later. */
const QUIET_MS = 200;

/** Scroll `el` into view within the nearest box that scrolls, then focus
 *  `target` (the first button that can be pressed when not given)
 *  without scrolling again. */
export function reveal(el: HTMLElement, target?: HTMLElement | null): void {
  const box = scrollBoxOf(el);
  const delta = box ? revealDelta(box.getBoundingClientRect(), el.getBoundingClientRect()) : 0;
  if (box && delta !== 0) {
    box.setAttribute('data-tuck-quiet', '');
    box.scrollTop += delta;
    window.setTimeout(() => box.removeAttribute('data-tuck-quiet'), QUIET_MS);
  }
  (target ?? firstEnabledButton(el))?.focus({ preventScroll: true });
}

/**
 * `reveal` the element the returned ref is put on, once, when it mounts.
 * `pick` chooses what takes focus. When it unmounts with focus lost
 * (its own button pressed, gone with it), focus goes back to what had it
 * when it opened -- the button that asked -- if that is still there.
 *
 * A question that REPLACES the button that asked (Remove → Keep / Remove
 * it) leaves nothing to go back to: that button unmounted as it opened.
 * `back` is a ref to the same button as it is drawn again once the
 * question goes, and focus goes there instead. Keep used to drop a
 * keyboard user's focus to the page (review BB-B-2, 2026-10-31).
 */
export function useRevealOnMount<T extends HTMLElement>(
  pick?: (el: T) => HTMLElement | null,
  back?: RefObject<HTMLElement | null>,
) {
  const ref = useRef<T>(null);
  const pickRef = useRef(pick);
  pickRef.current = pick;
  const backRef = useRef(back);
  backRef.current = back;
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    reveal(el, pickRef.current?.(el));
    return () => {
      const now = document.activeElement;
      const lost = !now || now === document.body || !now.isConnected || el.contains(now);
      if (!lost) return;
      const asker =
        before && before !== document.body && before.isConnected && !el.contains(before)
          ? before
          : (backRef.current?.current ?? null);
      if (asker?.isConnected) asker.focus({ preventScroll: true });
    };
  }, []);
  return ref;
}
